import { isFulfilled } from "@reduxjs/toolkit";
import { tenantTimeLineSteps, typeOrder } from "constants/AppConstants";
import TenantLayout from "pages/Layout/Tenant/TenantLayout";
import React, { useEffect, useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { TENANT_APPLICATION_COMPLETED } from "routes/tenantRoutes";
import { getAllErrorMessages, getTenantDetails, saveTenantPetData, stageOneSubmitHandler, tenantSubmitApplication } from "services/TenantSubmitHandler";
import { addUploadedDocument, markPetDocumentsLoaded, reindexUploadedDocuments, removeUploadDocByPetPos } from "store/formTemplate/formTemplateSlice";
import { fetchFormByProperty, fetchTenantFormTemplates } from "store/formTemplate/formTemplateThunks";
import { setIsValidAgent, setPrimartTenant, setTenantItems, setTenantSubmittedData } from "store/tenant/slice";
import { deletePetsData, verifyIsValidAgent } from "store/tenant/thunks";
import { DynamicItem, TenantBasicDetails, TenantPetsDocument } from "store/tenant/types";
import { FormTemplateItem, hasFieldsList } from "types/formTemplate";
import { getProfileTitle, isQldRegion, TENANT_LANDING_REGIONS } from "utils/petFormHelpers";
import TenantLanding from "./TenantLanding";
import { collectFieldNames } from "utils/renderTemplates";
import { convertToStageOneRequestedFormat, convertToStageTwoSubmittedFormat } from "utils/splitDot";
import { IconName } from "../../components/AnimalCard";
import AnimalProfile from "../../components/AnimalProfile/AnimalProfile";
import ErrorMessages from "../../components/UI/ErrorMessages";
import Modal from "../../components/UI/Modal";
import { useLoader } from "../../contexts/LoaderContext";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { getAnimalCardProfile, renderTemplates } from "../../utils/renderTemplates";
import { secureLocalStorage, secureSessionStorage } from "../../utils/secureStorage";
import Button from "./../../components/UI/Button";
import Stepper from "./../../components/UI/Stepper";

/**
 * Represents the values of the tenant form.
 *
 * @property {string} username - The username of the tenant.
 * @property {string} role - The role assigned to the tenant.
 */
export type FormValues = Record<string, any>;



/**
 * Tenant component renders the application form for tenants to apply for keeping a pet in a rental property.
 *
 * This component manages dynamic animal profile cards, fetches form templates from the store,
 * and renders the tenant-specific dynamic form fields. It provides functionality to add and remove
 * animal cards, and organizes them by type for consistent rendering. The form submission handler
 * logs the form data for further processing.
 *
 * @component
 * @returns {JSX.Element} The rendered tenant application form UI.
 *
 * @remarks
 * - Uses Redux for state management and fetching form templates.
 * - Utilizes React Hook Form for form handling and validation.
 * - Animal profile cards are dynamically rendered based on user interaction.
 * - Includes informational tooltips and external resource links for tenants.
 */
const Tenant: React.FC = () => {
  //Redux State selector and dispatch
  const disptach = useAppDispatch();
  const navigate = useNavigate();
  const methods = useForm<FormValues>({ mode: "onTouched", defaultValues: { tenants: {}} });
  const urlParts = window.location.pathname.split("/");
  const tenant_link = urlParts?.[2];
  const unique_link = urlParts?.[3];

  // const { agentId, formId } = useParams<{ agentId: string; formId: string }>();
  const { uniqueAgentId } = useParams<{ uniqueAgentId: string; }>();




  //Loader context
  const { showLoader, hideLoader } = useLoader();

  const { register, handleSubmit, getValues, watch, formState, unregister } = methods;
  const { isValid } = formState;



  const rawFormTemplate = useAppSelector((state) => state.formTemplate.templates);
  const primaryTenenatId = useAppSelector((state) => state.Tenant.primary_tenant_id);
  const primaryTenantDetails = useAppSelector((state) => state.Tenant.stageOneFullData);
  const uploadedDocumentsList = useAppSelector((state) => state.formTemplate.uploadedDocuments);
  const stageOnePetsData = useAppSelector((state) => state.Tenant.tenantPets);
  const stageOnePetsDocumentData = useAppSelector((state) => state.Tenant.tenantPetsDocuments);
  const tenantApplicationStatus = useAppSelector((state) => state.Tenant.tenantApplicationStatus);
  const formId = useAppSelector((state) => state.Tenant.agentFormId);
  const tenantitems = useAppSelector((state) => state.Tenant.tenantItems);
  const agentName = useAppSelector((state) => state.Tenant.agentName);
  const agentCompany = useAppSelector((state) => state.Tenant.agentCompany);
  const agentLogo = useAppSelector((state) => state.Tenant.agentLogo);
  const addressPlaceId = useAppSelector((state) => state.formTemplate.addressPlaceId);
  const [bottomBarHeight, setBottomBarHeight] = useState(0);
  const tenantPropertyId = useAppSelector((state) => state.Tenant.propertyId) || "";

  const handleUploadPetDocuments = React.useCallback((uploadedFiles: any) => {
    // Handle uploaded documents
  }, []);


  //Safe parse JSON
  let formTemplate: FormTemplateItem[] = [];
  try {
    if (Array.isArray(rawFormTemplate)) {
      formTemplate = rawFormTemplate;
    } else if (typeof rawFormTemplate === "string" && (rawFormTemplate as string).trim() !== "") {
      formTemplate = JSON.parse(rawFormTemplate as string);
    } else {
      formTemplate = [];
    }
  } catch (e) {
    formTemplate = [];
    // Failed to parse form template JSON
  }


  //State Declarations
  const [items, setItems] = useState<DynamicItem[]>([]);
  const [nextUid, setNextUid] = useState(1);
  const [activeStage, setActiveStage] = useState<number | null>(1);
  const [previousStage, setPreviousStage] = useState<number | null>(null);
  const [stageTwoData, setStageTwoData] = useState<FormValues | null>(null);
  const [formErrorMessages, setFormErrorMessages] = useState<string[]>([]);
  const [removedProfileTitle, setRemovedProfileTitle] = useState<string>("");
  const [originalAgentId, setOriginalAgentId] = useState<string>("");
  // QLD-only (region toggle): show the branded landing screen as the first thing on a fresh
  // pet-request entry. Region is read from the URL here so it's decided synchronously — the
  // localStorage region is set asynchronously and could race on first render. Only applies to
  // the pet-request entry (uniqueAgentId present); resumed applications are unaffected.
  const landingEntryRegion = window.location.pathname.includes("/pet-request-qld/") ? "qld" : "nsw";
  const [showLanding, setShowLanding] = useState<boolean>(
    !!uniqueAgentId && TENANT_LANDING_REGIONS.includes(landingEntryRegion)
  );

  // Watch form values on Stage 2 to trigger re-renders for areAllProfilesComplete()
  // This ensures the Next button updates when form fields change
  const formValues = activeStage === 2 ? watch() : null;
  void formValues; // Suppress unused variable warning while keeping subscription active

  // Use ref to track activeStage so popstate handler always reads current value
  const activeStageRef = useRef(activeStage);
  useEffect(() => {
    activeStageRef.current = activeStage;
  }, [activeStage]);

  const isValidAgent = useAppSelector((state) => state.Tenant.isValidAgent);


  const [showConfirmationMsg, setShowConfirmationMsg] = useState<DynamicItem | null>(null);
  // Only one profile open at a time
  const [openProfileUid, setOpenProfileUid] = useState<number | null>(null);
  // Refs for scrolling
  const profileRefs = useRef<{ [uid: number]: HTMLDivElement | null }>({});

  /**
   * Scroll to top whenever activeStage changes
   * This ensures smooth scroll on stage transitions
   */
  useEffect(() => {
    if (activeStage !== null) {
      // Use setTimeout to ensure DOM has updated before scrolling
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }

    // Only trigger validation when NAVIGATING TO stage 3 from another stage
    // This prevents validation from showing on initial page load (when previousStage is null)
    if (activeStage === 3 && previousStage !== null && previousStage !== 3) {
      // Trigger validation to ensure form state is synced with rendered fields when returning
      setTimeout(() => {
        methods.trigger();
      }, 150);
    }

    // Update previousStage to track stage transitions
    setPreviousStage(activeStage);
  }, [activeStage, tenantitems, methods, previousStage]);

  useEffect(() => {
    if (uniqueAgentId && uniqueAgentId !== "") {
      // Determine region based on URL path: /pet-request-qld/ = qld, /pet-request/ = nsw
      const region = window.location.pathname.includes("/pet-request-qld/") ? 'qld' : 'nsw';
      const verifyAgent = async (agentId: string) => {
        await disptach(verifyIsValidAgent({ agentId, region }));
      };
      verifyAgent(uniqueAgentId);
      // Store the original agentId for back button navigation
      setOriginalAgentId(uniqueAgentId);
      // Clear and set region in localStorage for use throughout the app
      localStorage.removeItem('pet_app_region');
      localStorage.setItem('pet_app_region', region);
      // Reset to stage 1 when on pet-request route
      setActiveStage(1);
      // Clear pet profile cards
      setItems([]);
      setNextUid(1);
      // Reset form state to clear all validation errors and field values
      // Pass empty object to completely clear all registered fields
      methods.reset({}, { keepDefaultValues: false });
      // Clear any existing tenant data from Redux to ensure form renders
      disptach(setPrimartTenant({
        success: true,
        message: "",
        data: {
          application_id: "",
          tenant_id: "",
          property: {
            id: "",
            address: "",
            notify_via_email: false,
            notify_via_sms: false,
            updated_at: "",
            created_at: ""
          },
          tenant_link: "",
          tenants: [],
          emails_sent: [],
          emails_sent_count: 0,
          uniqiue_link: ""
        }
      }));
      // Clear saved pets and form data from previous application
      disptach(setTenantSubmittedData({
        data: {
          property: {
            id: "",
            pets: [],
            tenants: [],
            address: ""
          },
          application: {}
        }
      }));
      // Clear tenantItems Redux state to prevent old pets from being submitted
      disptach(setTenantItems([]));
    } else {
      disptach(setIsValidAgent(false));
    }
  }, [uniqueAgentId, disptach]);

  //Fetch form templates on component mount
  const loadtemplates = React.useCallback(async () => {
    showLoader();
    try {
      const region = localStorage.getItem('pet_app_region') || undefined;
      const templateResponse = await disptach(fetchTenantFormTemplates({agentId: uniqueAgentId || "", region }));
      if (
        templateResponse?.payload &&
        typeof templateResponse.payload === "object" &&
        "success" in templateResponse.payload &&
        (templateResponse.payload as { success?: boolean }).success === false
      ) {
        setFormErrorMessages([
          (templateResponse.payload as { message?: string }).message || "Failed to load form templates."
        ]);
      }
      return templateResponse;
    } catch (error) {
      // Error fetching form templates
    } finally {
      hideLoader();
    }
  }, [showLoader, hideLoader, disptach, uniqueAgentId]);

    const loadtemplatesByProperty = React.useCallback(async () => {    
    try {
      // showLoader();
      const templateResponse = await disptach(fetchFormByProperty({propertyId: tenantPropertyId || ""}));
      if (
        templateResponse?.payload &&
        typeof templateResponse.payload === "object" &&
        templateResponse.payload !== null &&
        "success" in templateResponse.payload &&
        (templateResponse.payload as { success?: boolean }).success === false
      ) {
        setFormErrorMessages([
          (templateResponse.payload as { message?: string }).message || "Failed to load form templates."
        ]);
      }
      return templateResponse;
    } catch (error) {
      // Error fetching form templates
    } finally {
      // hideLoader();
    }
  }, [ disptach, tenantPropertyId]);

  // Form submission handler
  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    let result: any; // Declare result outside try block so it's accessible in finally
    try {
      
      showLoader();
      
      if(activeStage === 1) {
        const payload = convertToStageOneRequestedFormat(data, uniqueAgentId || "", formId || "", addressPlaceId || "");
        result = await disptach(stageOneSubmitHandler(payload));
        if(isFulfilled(result)){        
          const resultPayload = result.payload.data;
          const tenantLink = resultPayload.data.tenant_link;
          const uniqiueLink = resultPayload.data.uniqiue_link
          await disptach(getTenantDetails(uniqiueLink, tenantLink)) // Fetch tenant details after stage 1 submission
          if(tenantLink && uniqiueLink){
            setActiveStage(2);
            setFormErrorMessages([]);
            navigate(`/tenant/${uniqiueLink}/${tenantLink}`);
          } 
        }else if(result) {
          if(result.payload?.error !== ""){
            setFormErrorMessages([result.payload?.error || "An error occurred while submitting the form."]);
          }else{
            const errorMessages = getAllErrorMessages(result.payload);
            setFormErrorMessages(errorMessages);
          }
          
        }
      } else if(activeStage === 2) {
        setStageTwoData(data);
        const stageTwoFormat = convertToStageTwoSubmittedFormat(data, primaryTenantDetails, uploadedDocumentsList, unique_link, tenant_link, activeStage ?? undefined, tenantitems);
        result = await disptach(saveTenantPetData(stageTwoFormat, unique_link));
        await disptach(getTenantDetails(tenant_link, unique_link)); // TO get the refreshed data
        if(isFulfilled(result)){
          // Stage change triggers useEffect scroll
          setActiveStage(3);
        } else { 
          if(result){
            const errorMessages = 
              result?.payload && typeof result.payload === "object" && result.payload !== null && "message" in result.payload
                ? (result.payload as { message?: string }).message
                : undefined
            ;
            setFormErrorMessages([errorMessages || "An error occurred while submitting the form."]);
          }
        }
      } else {
        // Merge stage 2 and 3 data for final submission
        const mergedPayload = {
          ...convertToStageTwoSubmittedFormat(stageTwoData, primaryTenantDetails, uploadedDocumentsList, unique_link, tenant_link, activeStage ?? undefined, tenantitems),
          ...convertToStageTwoSubmittedFormat(data, primaryTenantDetails, uploadedDocumentsList, unique_link, tenant_link, activeStage ?? undefined, tenantitems),
        };
        const names = mergedPayload.tenants
          .map((group: { tenants: TenantBasicDetails[] }) =>
            group.tenants.map((t: { first_name: string; last_name: string }) => `${t.first_name} ${t.last_name}`)
          )
          .flat()
          .join(', ');

        secureLocalStorage.setItem('tenantNames', names);
        result = await disptach(tenantSubmitApplication(mergedPayload, unique_link));
        if(isFulfilled(result)){
          navigate(TENANT_APPLICATION_COMPLETED, { replace: true }) // Use replace to prevent back navigation
        } else {
          if(result){
            const errorMessages = 
              result?.payload && typeof result.payload === "object" && result.payload !== null && "message" in result.payload
                ? (result.payload as { message?: string }).message
                : undefined
            ;
            setFormErrorMessages([errorMessages || "An error occurred while submitting the form."]);
          }
        }
      }     
    } catch (error) {
      // Error submitting form
    } finally {
      // Scroll to top on stage change - only for error cases
      // Success cases handle scroll before state changes
      if (!isFulfilled(result)) {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
      hideLoader();
    }
  };

  /**
   * Check if all pet profiles on Stage 2 have required fields filled
   * This ensures button is disabled even when fields haven't been touched (respecting mode: "onTouched")
   * Mirrors the badge "Complete/Incomplete" logic from AnimalProfile.tsx
   */
  const areAllProfilesComplete = (): boolean => {
    if (activeStage !== 2 || items.length === 0) return true;

    const allValues = getValues();

    return items.every(item => {
      const { type } = item;
      const animalIndex = items.filter(i => i.type === type).indexOf(item);

      // Get all fields for this profile
      const profiles = getAnimalCardProfile(formTemplate, type)?.[0] || [];
      if (profiles.length === 0 || !profiles?.fieldsList) return true;

      const requiredFields = collectFieldNames(
        renderTemplates(
          profiles.fieldsList,
          { register, getValues, watch },
          animalIndex,
          type,
          stageOnePetsData
        )
      );

      if (requiredFields.length === 0) return true;

      // Check if this profile has required fields filled
      // Similar logic to AnimalProfile.tsx
      const microchipIdx = requiredFields.findIndex(f => f.includes("microchipNumber"));
      // Check for both NSW (choices) and QLD (registration_number) field names
      let registrationIdx = requiredFields.findIndex(f => f.includes("choices"));
      if (registrationIdx === -1) {
        registrationIdx = requiredFields.findIndex(f => f.includes("registration_number"));
      }

      if (microchipIdx !== -1 && registrationIdx !== -1) {
        // Dog/Cat: at least one of microchip/registration must be filled
        const microchipValue = getValueByPath(allValues, requiredFields[microchipIdx]);
        const registrationValue = getValueByPath(allValues, requiredFields[registrationIdx]);
        const atLeastOneFilled = (microchipValue !== undefined && microchipValue !== null && microchipValue !== "") ||
                                 (registrationValue !== undefined && registrationValue !== null && registrationValue !== "");

        const dontHaveReasonIdx = requiredFields.findIndex(f => f.includes("dont_have_reason"));
        const dontHaveReasonValue = dontHaveReasonIdx !== -1 ? getValueByPath(allValues, requiredFields[dontHaveReasonIdx]) : false;

        if (!dontHaveReasonValue && !atLeastOneFilled) return false;

        // Check other required fields
        return requiredFields.every((fieldPath, idx) => {
          if (idx === microchipIdx || idx === registrationIdx || idx === dontHaveReasonIdx) return true;

          const fieldValue = getValueByPath(allValues, fieldPath);
          const isEmpty = fieldValue === undefined || fieldValue === null || fieldValue === "";
          const fieldName = fieldPath.toLowerCase();
          const isOptional = fieldName.includes('supporting') || fieldName.includes('optional') ||
                           fieldName.includes('description2') || (isQldRegion() && fieldName.includes('.description.')) ||
                           (!isQldRegion() && fieldName.includes('fur_colour_length')) || (!isQldRegion() && fieldName.includes('weight_height_length')) ||
                           fieldName.includes('reason_for');

          return !isEmpty || isOptional;
        });
      } else {
        // Small mammals/other: check if any required field has value
        let hasAnyValue = false;
        for (const fieldPath of requiredFields) {
          const fieldValue = getValueByPath(allValues, fieldPath);
          const isEmpty = fieldValue === undefined || fieldValue === null || fieldValue === "";
          const fieldName = fieldPath.toLowerCase();
          const isOptional = fieldName.includes('supporting') || fieldName.includes('optional') ||
                           fieldName.includes('description2') || (isQldRegion() && fieldName.includes('.description.')) ||
                           (!isQldRegion() && fieldName.includes('fur_colour_length')) || (!isQldRegion() && fieldName.includes('weight_height_length')) ||
                           fieldName.includes('reason_for');

          if (!isEmpty) hasAnyValue = true;
          if (isEmpty && !isOptional) return false;
        }
        return hasAnyValue;
      }
    });
  };

  // Helper to get value by path
  const getValueByPath = (obj: any, path: string) => {
    return path.split(".").reduce((acc, part) => acc?.[part], obj);
  };

  /**
   * Adds a new card item to the list with the specified type and a unique identifier.
   *
   * @param type - The icon type for the new card item.
   */
  const handleAddCard = async (type: IconName) => {
    // QLD only: cap the total number of pets per application at 10.
    if (isQldRegion() && items.length >= 10) {
      toast.error("You can add a maximum of 10 pets per application.");
      return;
    }

    // Count how many items of this type already exist
    const sameTypeCount = items.filter(item => item.type === type).length;
    // animal_index will be sameTypeCount + 1
    const animal_index = sameTypeCount + 1;

    // Create the new item
    const newItem = { type, uid: nextUid, animal_index };

    // await disptach(getTenantDetails(tenant_link, unique_link)); // TO get the refreshed data

    setItems(prev => {
      const newItems = [...prev, newItem];
      // Only dispatch the newly added item to Redux
      disptach(setTenantItems([...tenantitems || [], newItem]));
      return newItems;
    });

    setNextUid(prev => prev + 1);
    setOpenProfileUid(nextUid);
    setTimeout(() => {
      const ref = profileRefs.current[nextUid];
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const confirmRemove = (item: DynamicItem) => {
    setShowConfirmationMsg(item); // store the full item
  };

  const deletePetData = async (pet_id: string) => {
    showLoader();
      try {
        await disptach(deletePetsData({ pet_id }));
      } catch (error) {
        // Error deleting pet data
      } finally {
        hideLoader();
      }
  }
  
  const handleRemoveConfirmed = async() => {
    if (showConfirmationMsg) {

      setShowConfirmationMsg(null); // Hide the popup
      
      showLoader();
      /**
       * Call deletePetData if pet_id exists before removing the profile from UI
       * Some pet may not saved to DB yet so at the time we can simply remove from UI
       * if it's saved then we should have pet_id and want to delete it from DB.
       */
      const currentPetIdToDelete = tenantitems && (tenantitems.find(item => item.uid === showConfirmationMsg.uid)?.pet_id || "");
      if(currentPetIdToDelete !== ""){
        await deletePetData(currentPetIdToDelete || "");
        // await disptach(getTenantDetails(tenant_link, unique_link)); // TO get the refreshed data
      }
      
      /**
       * CRITICAL: Mark the pet's documents as loaded (even though we're deleting it)
       * This prevents re-loading stale documents when adding new pets at the same position
       * Find the pet in stageOnePetsData to get its actual ID and mark it as "already processed"
       * This way, if someone adds a new pet at position 1 after deleting Dog 1, 
       * it won't load the old Dog 1's documents from stageOnePetsData
       */
      if (stageOnePetsData && stageOnePetsData.length > 0) {
        const type = showConfirmationMsg.type;
        const animal_index = showConfirmationMsg.animal_index;
        const sameTypePets = stageOnePetsData.filter(pet => pet.pet_type === type);
        const petToRemove = sameTypePets[animal_index - 1]; // animal_index is 1-based
        if (petToRemove && petToRemove.id) {
          disptach(markPetDocumentsLoaded(petToRemove.id));
        }
      }
      
      // Filter out the removed item from Redux first
      const filteredTenantItems = tenantitems ? tenantitems.filter((item) => item.uid !== showConfirmationMsg.uid) : [];
      if(tenantitems){
        await disptach(setTenantItems(filteredTenantItems));
      }

      const type = showConfirmationMsg.type;
      const uid = showConfirmationMsg.uid;

      // Get all items of the same type BEFORE removal
      const sameTypeItems = items.filter(item => item.type === type);
      const removedItemIndex = sameTypeItems.findIndex(item => item.uid === uid);

      // Remove the item from state
      setItems((prev) => prev.filter((item) => item.uid !== showConfirmationMsg.uid));
      // DO NOT decrement nextUid - it must always be unique and incrementing
      // setNextUid((prev) => prev - 1); // REMOVED: This causes uid conflicts
      
      // Remove documents for this specific pet
      disptach(removeUploadDocByPetPos({"animal_index": showConfirmationMsg.animal_index, "pet_type": showConfirmationMsg.type}));

      /**
       * CRITICAL FIX: Reindex field values for remaining pets of the same type
       * This ensures that when Dog 2 moves to Dog 1 position, its data moves with it
       */
      if (removedItemIndex !== -1) {
        // Collect all field names for this pet type
        const profiles = getAnimalCardProfile(formTemplate, type)?.[0] || [];
        let fieldsList = [];
        if (profiles?.fieldsList?.length > 0) {
          fieldsList = profiles.fieldsList;
        }

        // Get field name patterns (without index)
        const sampleFieldNames = collectFieldNames(
          renderTemplates(
            fieldsList,
            { register, getValues, watch: filteredWatch, handleAddCard, handleUploadPetDocuments },
            0,
            type,
            stageOnePetsData,
            stageOnePetsDocumentData
          )
        );

        // Extract field base names (e.g., "dog.microchipNumber" from "dog.microchipNumber.0")
        const fieldBaseNames = [
          ...sampleFieldNames.map(fieldName => {
            const parts = fieldName.split('.');
            if (parts.length === 3) {
              return `${parts[0]}.${parts[1]}`;
            }
            return fieldName;
          }),
          showConfirmationMsg?.type + ".reason_for_exemption",
          showConfirmationMsg?.type + ".description2"
        ].filter((value, index, self) => self.indexOf(value) === index); // unique values

        // Reindex: move values from old indices to new indices
        for (let i = removedItemIndex; i < sameTypeItems.length - 1; i++) {
          const currentIndex = i; // index being filled
          const nextIndex = i + 1; // index to copy from

          fieldBaseNames.forEach(baseName => {
            const sourceFieldName = `${baseName}.${nextIndex}`;
            const targetFieldName = `${baseName}.${currentIndex}`;
            const value = getValues(sourceFieldName);
            unregister(sourceFieldName);
            
            if (value !== undefined && value !== null) {
              methods.setValue(targetFieldName, value, { shouldValidate: false, shouldDirty: false });
              
            }
            methods.setValue(sourceFieldName, "", { shouldValidate: false, shouldDirty: false });
          });
        }

        // Unregister and clear the last position (now empty after shift)
        const lastIndex = sameTypeItems.length - 1;
        fieldBaseNames.forEach(baseName => {
          const lastFieldName = `${baseName}.${lastIndex}`;
          unregister(lastFieldName);
          methods.setValue(lastFieldName, "", { shouldValidate: false, shouldDirty: false });
        });

        /**
         * CRITICAL FIX: Reindex uploaded documents for the same pet type
         * This ensures documents are attached to the correct pet after removal
         * removedItemIndex is 0-based, but pet_index in documents is 1-based
         */
        disptach(reindexUploadedDocuments({
          pet_type: type,
          removedIndex: removedItemIndex + 1 // Convert to 1-based index
        }));

        /**
         * CRITICAL FIX: Reindex animal_index in items array
         * This ensures that when Dog 2 is removed, Dog 3 becomes Dog 2, etc.
         */
        setItems((prevItems) => 
          prevItems.map(item => {
            // Only update items of the same type that come after the removed position
            if (item.type === type && item.animal_index > showConfirmationMsg.animal_index) {
              return {
                ...item,
                animal_index: item.animal_index - 1
              };
            }
            return item;
          })
        );

        // Also update Redux tenantItems - use the already-filtered list
        if (filteredTenantItems.length > 0) {
          const updatedTenantItems = filteredTenantItems.map(item => {
            if (item.type === type && item.animal_index > showConfirmationMsg.animal_index) {
              return {
                ...item,
                animal_index: item.animal_index - 1
              };
            }
            return item;
          });
          disptach(setTenantItems(updatedTenantItems));
        }
      }

      toast.success("Pet profile removed");
      hideLoader();
    }
  };

  const handleKeep = () => {
    setShowConfirmationMsg(null); // just close modal
  };


  // Define the order of types for consistent rendering
 

  // Group items by type for ordered rendering dynamically
  const groupedItems: { [key in IconName]?: DynamicItem[] } = {};
  items.forEach((item) => {
    if (!groupedItems[item.type]) groupedItems[item.type] = [];
    groupedItems[item.type]!.push(item);
  });

  /**
   * Renders animal cards based on the provided animal type and index.
   * @param animalType  The type of animal (e.g., 'dog', 'cat', etc.).
   * @param animalIndex  The index of the animal card to render.
   * @returns JSX elements representing the animal cards or null if no profiles are found.
   */
  const renderAnimalCards = (animalType: string, animalIndex: number, petDocumentData?: TenantPetsDocument[], petPosition?: number) => {
    const profiles = getAnimalCardProfile(formTemplate, animalType)?.[0] || [];
    if (profiles.length === 0) return null;
    if (profiles?.fieldsList?.length > 0) {
      return renderTemplates(
        profiles?.fieldsList,
        { register, getValues, watch: filteredWatch, handleAddCard, handleUploadPetDocuments },
        animalIndex,
        animalType,
        stageOnePetsData,
        petDocumentData || [],
        "tenant",
        petPosition        
      );
    }
  };

  /**
   * Renders the dynamic tenant form based on the provided form type.
   * @param formType - The type of form to render, expected to be "dynamic_tenant_form".
   * @returns JSX elements representing the dynamic tenant form or null if not found.
   */
  const renderSection = (formType: string) => {
    const tenantForm = formTemplate.find(
      (template: FormTemplateItem) => template.type === formType
    );

    // hasFieldsList(tenantForm) added to ensure fieldsList exists and avoid typescript error
    if (tenantForm &&  hasFieldsList(tenantForm) && tenantForm?.type === formType) {
      return renderTemplates && renderTemplates(tenantForm?.fieldsList, {
        register,
        getValues,
        watch: filteredWatch,
        handleAddCard,
        handleUploadPetDocuments
      });
    }
    return null;
  };

  /**
   * Effect hook to conditionally fetch tenant details and form templates.
   *
   * This hook runs when the component mounts or when the `tenantId` or `applicationId` changes.
   */
  useEffect(() => {
    const fetchData = async () => {
      showLoader();
      try {
        // Check if we're on the pet-request route (not a tenant application route)
        // Matches both /pet-request/ and /pet-request-qld/
        const isPetRequestRoute = window.location.pathname.includes("/pet-request/") || window.location.pathname.includes("/pet-request-qld/");

        if (tenant_link && unique_link && !isPetRequestRoute) {
          // 1. Get tenant details first (only if we don't have property id yet)
          if (tenantPropertyId === "") {
            await disptach(getTenantDetails(tenant_link, unique_link));
          }

          if (tenantApplicationStatus === "agent_approved" || tenantApplicationStatus === "landlord_approved" || tenantApplicationStatus === "landlord_declined" || tenantApplicationStatus === "tenant_submitted") {
            secureLocalStorage.setItem('agent_name', agentName || "");
            secureLocalStorage.setItem('agent_company', agentCompany || "");
            secureSessionStorage.setItem('agent_name', agentName || "");
            secureSessionStorage.setItem('agent_company', agentCompany || "");
            navigate(TENANT_APPLICATION_COMPLETED, { replace: true }); // Use replace to prevent back navigation
            return; // Exit early to prevent further execution
          }

          // 2. Then load templates (only if property id exists)
          if (tenantPropertyId !== "") {
            await loadtemplatesByProperty();
          }
          setActiveStage(2);
        } else if(!window.location.pathname.includes("tenant") || isPetRequestRoute) {
          // Load templates if on pet-request route or no tenant/application id
          await loadtemplates();
        }
      } catch (error) {
        // Error in conditional API calls
        console.error('Error in fetchData:', error);
      } finally {
        hideLoader();
      }
    };

    fetchData();
    // Only run on mount or when dependencies change
  }, [loadtemplates, tenant_link, unique_link, tenantPropertyId, disptach, showLoader, hideLoader, loadtemplatesByProperty, tenantApplicationStatus, navigate, agentName, agentCompany]);

  const goToPrevious = async () => {
    const refreshData = await disptach(getTenantDetails(tenant_link, unique_link)); // TO get the refreshed data
    if(isFulfilled(refreshData)){
      setActiveStage(2);
      // Trigger form revalidation to update isValid state for current stage (with delay to ensure stage change is processed)
      setTimeout(() => {
        methods.trigger();
      }, 150);
    }
  }

  const handleSaveAndExit = async () => {
    showLoader();
    try {
      const currentData = getValues();
      const mergedPayload = {
        ...convertToStageTwoSubmittedFormat(stageTwoData, primaryTenantDetails, uploadedDocumentsList, unique_link, tenant_link, activeStage ?? undefined, tenantitems),
        ...convertToStageTwoSubmittedFormat(currentData, primaryTenantDetails, uploadedDocumentsList, unique_link, tenant_link, activeStage ?? undefined, tenantitems),
      };
      const result = await disptach(saveTenantPetData(mergedPayload, unique_link));
      if (isFulfilled(result)) {
        navigate(TENANT_APPLICATION_COMPLETED, { state: { saveAndExit: true }, replace: true });
      } else {
        if (result) {
          const errorMessages =
            result?.payload && typeof result.payload === "object" && result.payload !== null && "message" in result.payload
              ? (result.payload as { message?: string }).message
              : undefined;
          setFormErrorMessages([errorMessages || "An error occurred while saving the form."]);
        }
      }
    } catch (error) {
      // Error saving and exiting
    } finally {
      hideLoader();
    }
  };

  /**
   * Preapring to load the saved pet detials to animal profile cards
   * 1. Group the pets by type and maintain the order defined in typeOrder
   * 2. For each type, create DynamicItem entries with correct animal_index
   * 3. Set the items state with the prepared list
   * 4. Set nextUid to avoid uid conflicts when adding new cards
   */
  useEffect(() => {
  if (stageOnePetsData && stageOnePetsData.length > 0) {
    // Track count of each type to assign animal_index
    const typeCounts: Record<string, number> = {};
    const initialItems = stageOnePetsData.map((pet, idx) => {
      const type = pet.pet_type as IconName;
      typeCounts[type] = (typeCounts[type] || 0) + 1;
      return {
        type,
        uid: idx + 1, // or use pet.pet_number if unique
        animal_index: typeCounts[type],
        pet_id: pet.id || undefined
      };
    });
    setItems(initialItems);
    disptach(setTenantItems(initialItems));

    // Set nextUid to be higher than any existing uid (including any previously added items)
    // Use Math.max to handle both existing nextUid and loaded items
    const maxLoadedUid = Math.max(...initialItems.map(item => item.uid), 0);
    setNextUid((prevNextUid) => Math.max(prevNextUid, maxLoadedUid + 1));
  }
}, [stageOnePetsData]);

  const bottomBarRef = useRef<HTMLDivElement>(null);
 
  useEffect(() => {
    if (!bottomBarRef.current) return;

    const updateHeight = () => {
        setBottomBarHeight(bottomBarRef.current?.offsetHeight || 0);
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  
  // Import setIsValidAgent at the top of the file:
  // import { setIsValidAgent } from "store/tenant/slice";

  

  useEffect(() => {
    if(agentName && agentCompany){
      secureLocalStorage.setItem('agent_name', agentName || "");
      secureLocalStorage.setItem('agent_company', agentCompany || "");

      secureSessionStorage.setItem('agent_name', agentName || "");
      secureSessionStorage.setItem('agent_company', agentCompany || "");

    }
  }, [agentName, agentCompany]);

  useEffect(() => {
    const handleStatusChange = async () => {
      if (tenantApplicationStatus === "agent_approved" || tenantApplicationStatus === "landlord_approved" || tenantApplicationStatus === "landlord_declined" || tenantApplicationStatus === "tenant_submitted") {
        // Only proceed if agent name and company are available
        if (!agentName || !agentCompany) {
          return;
        }

        secureLocalStorage.setItem('agent_name', agentName);
        secureLocalStorage.setItem('agent_company', agentCompany);

        secureSessionStorage.setItem('agent_name', agentName);
        secureSessionStorage.setItem('agent_company', agentCompany);

        const tresultData = await disptach(getTenantDetails(tenant_link, unique_link));

        // Redirect only if the data fetch was successful
        if (isFulfilled(tresultData)) {
          navigate(TENANT_APPLICATION_COMPLETED, { replace: true }); // Use replace to prevent back navigation
        }
      }
    };
    handleStatusChange();
  }, [tenantApplicationStatus, agentName, agentCompany, disptach, tenant_link, unique_link, navigate]);

  // Disable bfcache by adding dummy event listeners
  useEffect(() => {
    const dummy = () => {};
    window.addEventListener('beforeunload', dummy);
    return () => window.removeEventListener('beforeunload', dummy);
  }, []);

  // Handle stage 3 back button - transition to stage 2 without popup
  useEffect(() => {
    if (activeStage !== 3) return;

    const handleStage3PopState = async () => {
      // Go to stage 2 without confirmation
      setActiveStage(2);
      const refreshData = await disptach(getTenantDetails(tenant_link, unique_link));
      if (isFulfilled(refreshData)) {
        setTimeout(() => methods.trigger(), 150);
      }
    };

    // Push state and attach listener for stage 3
    window.history.pushState({ stage: 3 }, "", window.location.pathname);
    window.addEventListener("popstate", handleStage3PopState);

    return () => window.removeEventListener("popstate", handleStage3PopState);
  }, [activeStage, tenant_link, unique_link, disptach, methods]);

  // Prevent browser back button on stage 2 - show confirmation popup
  useEffect(() => {
    if (activeStage !== 2) return;

    // Track if user landed directly on Stage 2 (e.g., from email link)
    const landedDirectlyOnStage2 = window.history.length <= 2; // <= 2 because initial load + pushState below

    const handleStage2PopState = () => {
      // If user landed directly on Stage 2 (from email), prevent back navigation entirely
      if (landedDirectlyOnStage2) {
        // Push state back to prevent navigation - no popup needed as there's nowhere to go back to
        window.history.pushState({ stage: 2 }, "", window.location.pathname);
        return;
      }

      // Normal flow: user came from Stage 1, show confirmation popup
      const userConfirmed = window.confirm(
        "Going back will create a new tenant application. You will have to resume this application from your email. Do you want to continue?"
      );

      if (userConfirmed) {
        const agentIdToUse = uniqueAgentId || originalAgentId;
        if (agentIdToUse) {
          const petRequestPath = localStorage.getItem('pet_app_region') === 'qld' ? '/pet-request-qld' : '/pet-request';
          navigate(`${petRequestPath}/${agentIdToUse}`);
        } else {
          navigate(`/tenant/${tenant_link}/${unique_link}`);
        }
      } else {
        // User cancelled - push state to stay on stage 2
        window.history.pushState({ stage: 2 }, "", window.location.pathname);
      }
    };

    // Push state and attach listener for stage 2
    window.history.pushState({ stage: 2 }, "", window.location.pathname);
    window.addEventListener("popstate", handleStage2PopState);

    return () => window.removeEventListener("popstate", handleStage2PopState);
  }, [activeStage, navigate, tenant_link, unique_link, uniqueAgentId, originalAgentId]);

    // Watch co_tenant_type and clear tenantForm if value is 'only_one_tenant'
  const coTenantType = watch('co_tenant_type');

  // Wrap watch to ignore UI-only fields (tooltips etc.) when used as a subscription callback
  const ignoredPatterns: Array<string | RegExp> = [
    'tooltip',
    "microchipNumber"
  ];
  const nameIsIgnored = (name?: string) => {
    if (!name) return false;
    return ignoredPatterns.some(p => typeof p === 'string' ? name === p || name.startsWith(p + '.') || name.includes(p) : p.test(name));
  };
  const wrapWatch = (origWatch: any) => {
    if (!origWatch) return origWatch;
    return (...args: any[]) => {
      if (args.length === 1 && typeof args[0] === 'function') {
        const cb = args[0];
        return origWatch((value: any, meta: any) => {
          const name = meta?.name;
          if (nameIsIgnored(name)) return;
          cb(value, meta);
        });
      }
      return origWatch(...args);
    };
  };
  const filteredWatch = wrapWatch(watch);
  const tenantFormList = [
    {id: 1},
    {id: 2}
  ]
  React.useEffect(() => {
    if (coTenantType === 'only_one_tenant') {
      tenantFormList.forEach((tenant) => {
        unregister(`tenants.${tenant.id}.firstName`);
        unregister(`tenants.${tenant.id}.lastName`);
        unregister(`tenants.${tenant.id}.email`);
        unregister(`tenants.${tenant.id}.mobile`);
      });
    }
  }, [coTenantType, unregister]);


  useEffect(() => {
    if(stageOnePetsData && stageOnePetsData.length > 0){
      stageOnePetsData.forEach(pet  => {
        if(pet?.support_documents && pet?.support_documents?.length > 0){
          pet?.support_documents.forEach((document) => {
            disptach(addUploadedDocument({
              document_id: document?.document_id || "",
              document_description: document?.description || "",
              pet_type: document?.pet_type || "",
              pet_index: document?.pet_number,
              original_filename: document?.original_filename,
              document_name: document?.original_filename,
              file_size: document?.file_size,
              property_id: document?.property_id,
          }));
        })
        }
      });
    }
  }, [stageOnePetsData, disptach]);

  // Sync notify_via_sms from API into the sms_notification form field
  useEffect(() => {
    if (primaryTenantDetails?.data?.property) {
      methods.setValue('sms_notification', primaryTenantDetails.data.property.notify_via_sms ?? false, { shouldValidate: false, shouldDirty: false });
    }
  }, [primaryTenantDetails, methods]);


  if (isValidAgent === false && activeStage === 1) {
    return (
      <TenantLayout>
        <div className="w-100 max-w-960px px-30px px-lg-0 mx-auto d-flex flex-column gap-40px">
          <div className="alert alert-danger mt-5 text-center">
            Invalid agent link. Please check the URL or contact your property manager.
          </div>
        </div>
      </TenantLayout>
    );
  }

  // QLD landing (stage 0): shown before the normal flow on a fresh pet-request entry.
  // "Start My Application" dismisses it and reveals the existing About-You form (Stage 1).
  if (showLanding) {
    return (
      <TenantLanding
        agentName={agentName}
        agentCompany={agentCompany}
        logo={agentLogo}
        onStart={() => setShowLanding(false)}
      />
    );
  }

  return (
    <TenantLayout roleType="tenant">
      <div className={`d-flex flex-column gap-40px pb-${bottomBarHeight}px pb-md-0`}>
        <div className="w-100 max-w-960px px-30px px-lg-0 mx-auto d-flex flex-column gap-40px">
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                // Prevent Enter key submission except in textarea elements
                if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                  e.preventDefault();
                }
              }}
              className="d-flex flex-column gap-40px"
            >

              

              {
                activeStage === 1 && formTemplate &&
                    formTemplate.length > 0 &&
                    renderSection("intro-text")
              }
              
              <Stepper
                steps={isQldRegion()
                  ? tenantTimeLineSteps.map((s, i) => i === 2 ? { label: 'Your Signature' } : s)
                  : tenantTimeLineSteps}
                currentStep={activeStage || 1}
              />

              {/**
               * Animal Cards Profile rendering logic moved to renderTemplates function
               */}
              {activeStage === 1 && primaryTenenatId === "" &&
                formTemplate &&
                formTemplate.length > 0 &&
                renderTemplates(formTemplate, { handleAddCard, register, handleUploadPetDocuments, getValues, watch: filteredWatch })}

                
              {/**
               * Section: 3
               * Dynamic Tenant Form rendering logic moved to renderTemplates function
               */}
              {activeStage === 2 && (
                <>
                  { 
                  // primaryTenenatId !== "" &&
                    formTemplate &&
                    formTemplate.length > 0 &&
                    renderSection("animal-profile")}

                  {items?.length === 0 &&
                    primaryTenenatId !== "" &&
                    formTemplate &&
                    formTemplate.length > 0 &&
                    renderSection("animal-card-placeholder")}

                  {/**
                   * Animal Cards Profile rendering logic moved to renderTemplates function
                   * and handled by rendertenentDynamicForm function.
                   */}
                  {items?.length > 0 && (
                    <div className="d-flex flex-column gap-20px text-black">
                      {typeOrder.map((type) =>
                        groupedItems[type]?.map((item, idx) => {
                          // Find pet data for this type and index
                          let petData = stageOnePetsData?.find(
                              pet => pet.pet_type === type && pet.pet_number === idx + 1
                            );
                          if(item.pet_id !== undefined){
                            petData = stageOnePetsData?.find(
                              pet => pet.pet_id === item.pet_id
                            );
                          }
                          // Find document data for this pet (customize as needed)
                          const petDocumentData = petData?.support_documents || [];
                          // Assign ref for scrolling
                          return (
                            <div
                              key={item.uid}
                              ref={el => { profileRefs.current[item.uid] = el; }}
                            >
                              <AnimalProfile
                                type={item.type}
                                title={getProfileTitle(type, idx)}
                                onRemove={(profileMainTitle, profileSubHeaderText, uid) => {
                                  setRemovedProfileTitle(profileMainTitle.trim());
                                  // setRemovedProfileItem
                                  confirmRemove(item);
                                }}
                                animalType={type}
                                petData={petData ? [petData] : undefined}
                                petDocumentData={petDocumentData.length > 0 ? petDocumentData : undefined}
                                collapse={item.uid !== openProfileUid}
                                // onOpen={() => {
                                //   setOpenProfileUid(item.uid);
                                //   setTimeout(() => {
                                //     const ref = profileRefs.current[item.uid];
                                //     if (ref) {
                                //       ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                //     }
                                //   }, 100);
                                // }}
                                onOpen={() => {
                                  setOpenProfileUid(openProfileUid === item.uid ? null : Number(item.uid));
                                  setTimeout(() => {
                                    const ref = profileRefs.current[item.uid];
                                    if (ref) {
                                      ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }
                                  }, 100);
                                }}
                                keyIndex={idx}
                                isModal={false}
                                unique_link={unique_link || ""}
                                tenant_link={tenant_link || ""}
                                animal_profile_id={petData?.pet_id || ""}
                                uid={item.uid}
                              >
                                {formTemplate &&
                                  formTemplate.length > 0 &&
                                  renderAnimalCards(type, idx, petDocumentData, idx + 1)}
                              </AnimalProfile>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </>
              )}

              {/**
               * Section: 4
               * Dynamic Tenant Form rendering logic moved to renderTemplates function
               * and handled by rendertenentDynamicForm function.
               */}
              {activeStage === 3 && (
                <>
                  {primaryTenenatId !== "" &&
                    formTemplate &&
                    formTemplate.length > 0 &&
                    renderSection("dynamic_tenant_form")}
                  {/**
                   * Section: 5
                   * Signature of Tenants rendering logic moved to renderTemplates function
                   * and handled by rendertenentDynamicForm function.
                   */}
                  {primaryTenenatId !== "" &&
                    formTemplate &&
                    formTemplate.length > 0 &&
                    renderSection("signature_of_tenants")}

                  {primaryTenenatId !== "" &&
                    formTemplate &&
                    formTemplate.length > 0 &&
                    renderSection("tenant_general_consent")}

                  {/**
                   * Section: 6
                   * Info Section rendering logic moved to renderTemplates function
                   */}
                  {primaryTenenatId !== "" &&
                    formTemplate &&
                    formTemplate.length > 0 &&
                    renderSection("info-section")}
                </>
              )}

              <ErrorMessages apiResponse={formErrorMessages} />

             

              <div ref={bottomBarRef} className="d-flex flex-column flex-md-row gap-20px justify-content-md-between position-xs-fixed bottom-0 start-0 end-0 bg-white p-30px p-md-0 drop-shadow-xs-2">
                {formTemplate && formTemplate.length > 0 && (
                  <>

                    {
                      activeStage && activeStage >= 3 ? (
                        <Button type="button" iconPosition="left" text="Previous Step" iconName="arrowLeft" onClick={goToPrevious}/>
                      ) : (
                        <div className="d-none d-md-block">{" "}</div>
                      )
                    }
                    <div className="d-flex flex-column flex-md-row gap-20px">
                      {activeStage === 3 && isQldRegion() && (
                        <Button
                          type="button"
                          text="Save & Exit"
                          iconName="check"
                          iconPosition="left"
                          className="btn-theme bg-grey-4 text-black"
                          onClick={handleSaveAndExit}
                        />
                      )}
                      <Button
                        iconName={activeStage === 3 ? "check" : "arrowRight"}
                        iconPosition={activeStage === 3 ? "left" : "right"}
                        text={primaryTenenatId === null || activeStage !== 3 ? "Next Step" : (isQldRegion() ? "Send To Me For Signing" : "Submit")}
                        // Stage-specific validation: each stage validates only its own requirements
                        disabled={
                          (activeStage === 1 && !isValid) ||
                          (activeStage === 2 && (items?.length === 0 || !isValid || !areAllProfilesComplete())) ||
                          (activeStage === 3 && !isValid)
                        }
                      />
                    </div>
                  </>
                )}
              </div>
            </form>
          </FormProvider>


          {showConfirmationMsg !== null && (
            <Modal onClose={handleKeep}>
              <div className="d-flex flex-column gap-15px p-30px text-black">
                <div className="fs-18px fw-bold">
                  You're about to remove an animal profile
                </div>
                <div className="fs-16px">
                  Any information you've added to the profile "
                  {removedProfileTitle}
                  " will be removed. This action cannot be undone.
                </div>
                <div className="d-flex flex-column flex-md-row gap-20px justify-content-md-end">
                  <button
                    type="button"
                    className="btn-theme bg-grey-4 text-black"
                    onClick={handleKeep}
                  >
                    Keep Profile
                  </button>
                  <button
                    type="button"
                    className="btn-theme bg-grey-1 text-white"
                    onClick={handleRemoveConfirmed}
                  >
                    Remove Profile
                  </button>
                </div>
              </div>
            </Modal>
          )}
        </div>
      </div>
    </TenantLayout>
  );
};

export default Tenant;
